import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Form, Input, Modal, Select, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { Page } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { DateRangePicker as ZhDateRangePicker, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import type { ServiceCostBill } from "@/entity/expenseManage/serviceCostBill";
import { BILL_STATE, SERVICE_COST_TYPE } from "@/meta/expenseManage/serviceCost";
import { ROLE_CODE } from "@/meta/customer/customer";
import { Permission } from "@/views/components/Permission";
import { useUserStore } from "@/stores/react/authStore";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import {
    ServiceCostBillVerificationModal,
    type ServiceCostBillVerificationValues
} from "./ServiceCostBillVerificationModal";
import { revokeServiceCostBill, verifyServiceCostBill } from "./ServiceCostBillDetailPage";

export interface ServiceCostBillRow extends ServiceCostBill {
    customerInfo?: { institutionName?: string; realName?: string; mobile?: string; code?: string };
}

export interface ServiceCostBillQuery {
    page: number;
    size: number;
    keyword?: string;
    customerInfo?: string;
    roleCode?: number;
    verificationState?: number;
    startTime?: string;
    endTime?: string;
    sortKey?: string;
    sortOrder?: "asc" | "desc";
}

export interface ServiceCostBillPageProps {
    loadBills?: (query: ServiceCostBillQuery) => Promise<Page<ServiceCostBillRow>>;
    verifyBill?: (params: {
        id: number | string;
        verificationAmount: number;
        verificationTime: string;
        verificationFileId?: number;
    }) => Promise<unknown>;
    revokeBill?: (id: number | string) => Promise<unknown>;
}

// 沿用 Vue useTable 的默认分页契约，每页展示 10 条账单。
const defaultQuery: ServiceCostBillQuery = { page: 1, size: 10 };

/** 将 Vue 待办入口的 state 查询参数转换为服务费列表的接口筛选字段。 */
const getInitialServiceCostBillQuery = (state: string | null): ServiceCostBillQuery => {
    const verificationState = state === null ? Number.NaN : Number(state);
    return Number.isFinite(verificationState) ? { ...defaultQuery, verificationState } : { ...defaultQuery };
};

/** 复用 Vue 服务费账单分页接口。 */
export async function loadServiceCostBills(query: ServiceCostBillQuery): Promise<Page<ServiceCostBillRow>> {
    const response = await request.service.get<Page<ServiceCostBillRow>>("/admin/service-cost-bill/paging/find-list", {
        params: query
    });
    return response.data;
}

const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);
const formatAmount = (value?: number | null) =>
    value === undefined || value === null
        ? DEFAULT_VALUE
        : Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** 保留 Vue 用户账户列的 JSON 信息展示顺序。 */
const formatCustomer = (customerInfo?: ServiceCostBillRow["customerInfo"]) => {
    if (!customerInfo) return DEFAULT_VALUE;
    return `${customerInfo.institutionName || customerInfo.realName || DEFAULT_VALUE} | ${customerInfo.mobile || DEFAULT_VALUE} | ${customerInfo.code || DEFAULT_VALUE}`;
};

/** 费用管理-服务费账单列表，完整复用旧 Vue 的字段、筛选和分页契约。 */
export function ServiceCostBillPage({
    loadBills = loadServiceCostBills,
    verifyBill = verifyServiceCostBill,
    revokeBill = revokeServiceCostBill
}: ServiceCostBillPageProps) {
    const banButton = useUserStore(state => state.banButton);
    const [searchParams] = useSearchParams();
    // 我的任务沿用 Vue 的 state 参数进入列表，首屏同时回显状态筛选并提交给接口。
    const initialQuery = getInitialServiceCostBillQuery(searchParams.get("state"));
    const [rows, setRows] = useState<ServiceCostBillRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<ServiceCostBillQuery>(initialQuery);
    const [keyword, setKeyword] = useState("");
    const [customerInfo, setCustomerInfo] = useState("");
    const [roleCode, setRoleCode] = useState("");
    const [verificationState, setVerificationState] = useState(
        initialQuery.verificationState === undefined ? "" : String(initialQuery.verificationState)
    );
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [verificationBill, setVerificationBill] = useState<ServiceCostBillRow>();
    const [verificationOpen, setVerificationOpen] = useState(false);
    const [verificationLoading, setVerificationLoading] = useState(false);
    const [revokeBillRow, setRevokeBillRow] = useState<ServiceCostBillRow>();
    const [revokeOpen, setRevokeOpen] = useState(false);
    const [revokeLoading, setRevokeLoading] = useState(false);

    useEffect(() => {
        let disposed = false;
        void loadBills(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("服务费账单加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadBills, query]);

    // 将 Vue ProTable 筛选草稿转换为服务费账单分页参数，并归一到第一页。
    const submitFilters = () => {
        if (startDate && endDate && startDate > endDate) {
            setErrorMessage("结束时间不能早于开始时间");
            return;
        }
        const nextQuery: ServiceCostBillQuery = { ...defaultQuery };
        if (keyword.trim()) nextQuery.keyword = keyword.trim();
        if (customerInfo.trim()) nextQuery.customerInfo = customerInfo.trim();
        if (roleCode) nextQuery.roleCode = Number(roleCode);
        if (verificationState) nextQuery.verificationState = Number(verificationState);
        if (startDate) nextQuery.startTime = `${startDate} 00:00:00`;
        if (endDate) nextQuery.endTime = `${endDate} 23:59:59`;
        setErrorMessage("");
        setQuery(nextQuery);
    };

    // 清空筛选并恢复服务费账单首屏参数。
    const resetFilters = () => {
        setKeyword("");
        setCustomerInfo("");
        setRoleCode("");
        setVerificationState("");
        setStartDate("");
        setEndDate("");
        setErrorMessage("");
        setQuery(defaultQuery);
    };

    // 操作成功后沿用当前 Vue ProTable 查询参数刷新列表，保留筛选和分页位置。
    const reloadBills = async () => {
        try {
            const result = await loadBills(query);
            setRows(result.list || []);
            setTotal(result.total || 0);
            setErrorMessage("");
        } catch {
            setErrorMessage("服务费账单刷新失败");
        }
    };

    // 复用 Vue 核销弹窗字段和接口 payload，成功后关闭弹窗并刷新列表。
    const submitVerification = async (values: ServiceCostBillVerificationValues) => {
        if (!verificationBill) return;
        setVerificationLoading(true);
        try {
            await verifyBill({
                id: verificationBill.id,
                verificationAmount: values.amount,
                verificationTime: values.date,
                verificationFileId: values.fileId
            });
            notifyOperationSuccess("核销成功！");
            await reloadBills();
            setVerificationOpen(false);
            setVerificationBill(undefined);
        } catch {
            setErrorMessage("账单核销失败");
        } finally {
            setVerificationLoading(false);
        }
    };

    // 复用 Vue 撤销确认文案和接口，成功后刷新当前账单列表。
    const submitRevoke = async () => {
        if (!revokeBillRow) return;
        setRevokeLoading(true);
        try {
            await revokeBill(revokeBillRow.id);
            notifyOperationSuccess("撤销成功！");
            await reloadBills();
            setRevokeOpen(false);
            setRevokeBillRow(undefined);
        } catch {
            setErrorMessage("账单撤销失败");
        } finally {
            setRevokeLoading(false);
        }
    };

    // 用 zhihao-ui-react 列契约承接 Vue 服务费账单字段、状态、详情和核销操作。
    const columns: TableColumnProps<ServiceCostBillRow>[] = [
        {
            key: "id",
            label: "编号",
            width: 150,
            prop: "id",
            render: ({ row }) => <Link to={`/expenseManagement/serviceCostBill/detail/${row.id}`}>{row.id}</Link>
        },
        {
            key: "customerInfo",
            label: "用户账户",
            minWidth: 400,
            prop: "customerInfo",
            render: ({ row }) => formatCustomer(row.customerInfo)
        },
        {
            key: "roleCode",
            label: "身份",
            width: 140,
            prop: "roleCode",
            render: ({ row }) => ROLE_CODE.getIntroByCode(row.roleCode) || DEFAULT_VALUE
        },
        {
            key: "projectName",
            label: "关联项目",
            minWidth: 228,
            prop: "projectName",
            render: ({ row }) =>
                row.projectId ? (
                    <Link to={`/purchasingManage/project/info/${row.projectId}`}>
                        {row.projectName || DEFAULT_VALUE}
                    </Link>
                ) : (
                    row.projectName || DEFAULT_VALUE
                )
        },
        {
            key: "amount",
            label: "账单金额（元）",
            minWidth: 174,
            prop: "amount",
            render: ({ row }) => formatAmount(row.amount)
        },
        {
            key: "verificationAmount",
            label: "实际核销金额（元）",
            minWidth: 174,
            prop: "verificationAmount",
            render: ({ row }) =>
                row.verificationState === BILL_STATE.NOT_WRITTEN_OFF.code
                    ? DEFAULT_VALUE
                    : formatAmount(row.verificationAmount)
        },
        {
            key: "type",
            label: "结算方式",
            minWidth: 164,
            prop: "type",
            render: ({ row }) => SERVICE_COST_TYPE.getIntroByCode(row.type) || DEFAULT_VALUE
        },
        {
            key: "updatedTime",
            label: "更新时间",
            minWidth: 170,
            prop: "updatedTime",
            sortable: true,
            render: ({ row }) => formatDate(row.updatedTime)
        },
        {
            key: "verificationState",
            label: "状态",
            width: 100,
            prop: "verificationState",
            render: ({ row }) => BILL_STATE.getIntroByCode(row.verificationState) || DEFAULT_VALUE
        },
        {
            key: "operation",
            label: "操作",
            fixed: "right",
            width: 200,
            prop: "id",
            render: ({ row }) =>
                row.verificationState === BILL_STATE.NOT_WRITTEN_OFF.code ? (
                    <Permission anyOf={["ROLE_ACCOUNT_VERIFY"]}>
                        <Button
                            disabled={banButton}
                            type="link"
                            onClick={() => {
                                setVerificationBill(row);
                                setVerificationOpen(true);
                            }}
                        >
                            核销
                        </Button>
                    </Permission>
                ) : (
                    <Permission anyOf={["ROLE_ACCOUNT_VERIFY"]}>
                        <Button
                            disabled={banButton}
                            type="link"
                            onClick={() => {
                                setRevokeBillRow(row);
                                setRevokeOpen(true);
                            }}
                        >
                            撤销
                        </Button>
                    </Permission>
                )
        }
    ];

    return (
        <section aria-label="service-cost-bill-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col">
                <ZhTable<ServiceCostBillRow>
                    data={rows}
                    columns={normalizeOperationColumns(columns, rows)}
                    pagination={false}
                    rowKey="id"
                    onSortChange={({ prop, order }) => {
                        if (prop !== "updatedTime") return;
                        setQuery(current => {
                            const nextQuery: ServiceCostBillQuery = { ...current, page: 1 };
                            delete nextQuery.sortKey;
                            delete nextQuery.sortOrder;
                            if (order) {
                                nextQuery.sortKey = "updated_time";
                                nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                            }
                            return nextQuery;
                        });
                    }}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                服务费账单
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form className="admin-table-filter-form mb-4" layout="inline" onFinish={submitFilters}>
                                <Form.Item label="编号或项目名称">
                                    <Input
                                        aria-label="编号或项目名称"
                                        placeholder="请输入编号或项目名称搜索"
                                        value={keyword}
                                        onChange={event => setKeyword(event.target.value)}
                                    />
                                </Form.Item>
                                <Form.Item label="账号信息">
                                    <Input
                                        aria-label="账号信息"
                                        placeholder="请输入账号信息搜索"
                                        value={customerInfo}
                                        onChange={event => setCustomerInfo(event.target.value)}
                                    />
                                </Form.Item>
                                <Form.Item label="身份">
                                    <Select
                                        allowClear
                                        aria-label="身份"
                                        options={ROLE_CODE.options
                                            .filter(option => option.value !== ROLE_CODE.CARRIER.code)
                                            .map(option => ({ label: option.label, value: String(option.value) }))}
                                        placeholder="请选择身份"
                                        value={roleCode || undefined}
                                        onChange={value => setRoleCode(value || "")}
                                    />
                                </Form.Item>
                                <Form.Item label="状态">
                                    <Select
                                        allowClear
                                        aria-label="状态"
                                        options={BILL_STATE.options.map(option => ({
                                            label: option.label,
                                            value: String(option.value)
                                        }))}
                                        placeholder="请选择状态"
                                        value={verificationState || undefined}
                                        onChange={value => setVerificationState(value || "")}
                                    />
                                </Form.Item>
                                <Form.Item label="更新时间">
                                    <ZhDateRangePicker
                                        clearable
                                        endPlaceholder="结束时间"
                                        format="YYYY-MM-DD"
                                        rangeSeparator="-"
                                        startPlaceholder="起始时间"
                                        type="daterange"
                                        value={startDate || endDate ? [startDate, endDate] : null}
                                        valueFormat="YYYY-MM-DD"
                                        onChange={(_value, formattedValue) => {
                                            setStartDate(formattedValue?.[0] || "");
                                            setEndDate(formattedValue?.[1] || "");
                                        }}
                                    />
                                </Form.Item>
                                <Space>
                                    <Button aria-label="搜索" htmlType="submit" type="primary">
                                        搜索
                                    </Button>
                                    <Button onClick={resetFilters}>重置</Button>
                                </Space>
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                total={total}
                                hideOnSinglePage
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
            <ServiceCostBillVerificationModal
                open={verificationOpen}
                billAmount={verificationBill?.amount}
                loading={verificationLoading}
                onCancel={() => setVerificationOpen(false)}
                onSubmit={values => void submitVerification(values)}
            />
            <Modal
                title="撤销账单"
                open={revokeOpen}
                okText="确定"
                cancelText="取消"
                confirmLoading={revokeLoading}
                okButtonProps={{ disabled: banButton }}
                onCancel={() => setRevokeOpen(false)}
                onOk={() => void submitRevoke()}
            >
                <Typography.Text>是否撤销，撤销后账户余额将发生变化，请谨慎操作</Typography.Text>
            </Modal>
        </section>
    );
}
